import {
    animate,
    animateChild,
    group,
    query,
    style,
    transition,
    trigger
} from '@angular/animations';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input
} from '@angular/core';
import { Observable, ReplaySubject, Subject, timer } from 'rxjs';
import {
    finalize,
    map,
    repeatWhen,
    shareReplay,
    takeUntil,
    takeWhile,
    withLatestFrom
} from 'rxjs/operators';
import { AsyncPipe, CommonModule } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
export type IndicatorState = 'loading' | 'success' | 'error' | 'close' | number | null;
@Component({
    selector: 'sb-pending-button-indicator',
    standalone: true,
    imports: [CommonModule, AsyncPipe],
    template: `
        <svg
            @state
            *ngIf="state"
            viewBox="0 0 24 24"
            height="24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
            stroke-width="2"
            stroke="currentColor"
            fill="none"
            stroke-linecap="square"
            stroke-linejoin="round"
            [class.sb-progress]="isProgress"
        >
            <g @loading *ngIf="state === 'loading'" class="loading">
                <circle cx="12" cy="12" r="9" />
            </g>
            <g @success *ngIf="state === 'success'" class="success">
                <circle class="outer" cx="12" cy="12" r="9" />
                <path class="check" d="M9 12l2 2l4 -4"></path>
            </g>
            <g @error *ngIf="state === 'error'" class="errors">
                <circle class="outer" cx="12" cy="12" r="9" />
                <line class="line" x1="12" y1="8" x2="12" y2="12"></line>
                <line class="dot" x1="12" y1="16" x2="12.01" y2="16"></line>
            </g>
            <g @close *ngIf="state === 'close'">
                <circle class="outer" cx="12" cy="12" r="9"></circle>
                <path class="cross" d="M10 10l4 4m0 -4l-4 4"></path>
            </g>
            <g @countdown *ngIf="isCountdown">
                <circle
                    [style.--countdown-duration]="(countdownDuration$ | async) + 's'"
                    class="sb-countdown-circle"
                    cx="12"
                    cy="12"
                    r="9"
                />
            </g>
            <g *ngIf="isProgress">
                <circle
                    [style.--progress-offset]="(progress$ | async)?.offset"
                    class="sb-progress-circle"
                    cx="12"
                    cy="12"
                    r="9"
                />
            </g>
        </svg>
        <span
            @state
            class="sb-progress-content"
            *ngIf="state && state !== 'success' && isProgress"
        >
            {{ (progress$ | async)?.percent }}
        </span>
        <span
            @state
            class="sb-countdown"
            *ngIf="state && isCountdown && countdownTimeLeft$ | async"
        >
            {{ (countdownTimeLeft$ | async) - 1 }}
        </span>
    `,
    styles: [
        `
            :host {
                margin: -0.5rem -0.25rem -0.5rem 0.5rem;
                display: inline-flex;
            }
            .sb-countdown {
                position: absolute;
                width: 22px;
                height: 22px;
                text-align: center;
                line-height: 22px;
                font-size: 10px;
            }
            .sb-countdown-circle {
                stroke-dasharray: 57;
                stroke-dashoffset: -57;
                animation: countdown-animation linear 1;
                animation-duration: var(--countdown-duration);
                animation-delay: 200ms;
            }
            @keyframes countdown-animation {
                0% {
                    stroke-dashoffset: 0;
                }
                100% {
                    stroke-dashoffset: -57;
                }
            }

            .sb-progress-content {
                position: absolute;
                width: 24px;
                height: 24px;
                text-align: center;
                line-height: 24px;
                font-size: 6.5px;
                font-weight: 800;
            }
            .sb-progress-circle {
                stroke-dasharray: 57;
                stroke-dashoffset: var(--progress-offset, -57);
                transform: rotate(90deg);
                transform-origin: center;
            }
        `
    ],
    animations: [
        trigger('state', [
            transition(':enter', [
                style({ opacity: 0, width: 0 }),
                group([
                    animate('300ms ease-in', style({ opacity: 1, width: '22px' })),
                    query('@loading', animateChild(), { optional: true }),
                    query('@*', animateChild(), { optional: true })
                ])
            ]),
            transition(':leave', [
                group([
                    query(
                        '.sb-countdown',
                        [style({ scale: 2 }), animate('3000ms', style({ scale: 0 }))],
                        { optional: true }
                    ),

                    query('@loading', animateChild(), { optional: true }),
                    query('@*', animateChild(), { optional: true }),
                    animate('300ms 600ms ease-out', style({ opacity: 0, width: 0 }))
                ])
            ])
        ]),
        trigger('loading', [
            transition(':enter', [
                query('circle', [
                    style({ opacity: 0 }),
                    animate('300ms ease-in-out', style({ opacity: 1 }))
                ])
            ]),
            transition(':leave', [
                query('circle', [animate('300ms ease-in-out', style({ opacity: 0 }))])
            ])
        ]),
        trigger('countdown', [
            transition(':enter', [
                group([
                    query('circle', [
                        style({ opacity: 0, strokeDashoffset: 57, strokeDasharray: 57 }),
                        animate(
                            '500ms ease-in-out',
                            style({ opacity: 1, strokeDashoffset: 0 })
                        )
                    ])
                ])
            ]),
            transition(':leave', [
                query('circle.sb-countdown-circle', [
                    animate('500ms 300ms ease-in-out', style({ opacity: 0 }))
                ])
            ])
        ]),
        trigger('success', [
            transition(':enter', [
                group([
                    query('circle.outer', [
                        style({ opacity: 0, strokeDashoffset: 57, strokeDasharray: 57 }),
                        animate(
                            '500ms ease-in-out',
                            style({ opacity: 1, strokeDashoffset: 0 })
                        )
                    ]),
                    query('path.check', [
                        style({ opacity: 0, strokeDashoffset: 10, strokeDasharray: 10 }),
                        animate(
                            '500ms 300ms ease-in-out',
                            style({ opacity: 1, strokeDashoffset: 0 })
                        )
                    ])
                ])
            ]),
            transition(':leave', [
                query('path.check', [
                    style({ opacity: 1, strokeDasharray: 10 }),
                    animate(
                        '500ms ease-in-out',
                        style({ opacity: 0, strokeDashoffset: -10, strokeDasharray: 10 })
                    )
                ]),
                query('circle.outer', [
                    animate(
                        '500ms 300ms ease-in-out',
                        style({ opacity: 0, strokeDashoffset: -57 })
                    )
                ])
            ])
        ]),
        trigger('error', [
            transition(':enter', [
                group([
                    query('circle.outer', [
                        style({ opacity: 0 }),
                        animate('500ms ease-in-out', style({ opacity: 1 }))
                    ]),
                    query('.line', [
                        style({ opacity: 0 }),
                        animate('300ms 400ms ease-out', style({ opacity: 1 }))
                    ]),
                    query('.dot', [
                        style({ opacity: 0 }),
                        animate('300ms 650ms ease-in', style({ opacity: 1 }))
                    ])
                ])
            ]),
            transition(':leave', [
                query('.dot', [animate('300ms ease-in', style({ opacity: 0 }))]),
                query('.line', [animate('300ms ease-out', style({ opacity: 0 }))]),
                query('circle.outer', [
                    animate('500ms ease-in-out', style({ opacity: 0 }))
                ])
            ])
        ]),
        trigger('close', [
            transition(':enter', [
                group([
                    query('circle.outer', [
                        style({ opacity: 0, strokeDashoffset: 57, strokeDasharray: 57 }),
                        animate(
                            '500ms ease-in-out',
                            style({ opacity: 1, strokeDashoffset: 0 })
                        )
                    ]),
                    query('.cross', [
                        style({ opacity: 0, scale: 0, transformOrigin: 'center' }),
                        animate('400ms 400ms ease-out', style({ opacity: 1, scale: 1 }))
                    ])
                ])
            ]),
            transition(':leave', [
                query('.cross', [
                    style({ opacity: 1, scale: 1, transformOrigin: 'center' }),
                    animate('500ms ease-out', style({ opacity: 0, scale: 0 }))
                ]),
                query('circle.outer', [
                    animate(
                        '500ms ease-in-out',
                        style({ opacity: 0, strokeDashoffset: -57 })
                    )
                ])
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbPendingButtonIndicatorComponent {
    get isProgress(): boolean {
        return this._isProgress;
    }

    @Input() set isProgress(value: boolean) {
        this._isProgress = value;
    }
    get resetState(): boolean {
        return this._resetState;
    }

    @Input() set resetState(value: boolean) {
        this._resetState = coerceBooleanProperty(value);
    }
    timer: NodeJS.Timeout;
    get state(): IndicatorState {
        return this._state;
    }

    @Input() set state(value: IndicatorState) {
        this.cancelTimer$.next(true);
        if (this.timer) {
            clearTimeout(this.timer);
        }
        if (this.isProgress && (typeof value === 'number' || !isNaN(value as any))) {
            this._state = +value;
            const percent = Math.round(+value * 100);
            if (percent >= 100) {
                this.progress$.next(null);
                this.state = 'success';
                return;
            }
            this.progress$.next({
                percent: percent + '%',
                offset: -57 - +value * 57
            });
            return;
        } else if (typeof value === 'number' || !isNaN(value as any)) {
            this._state = +value;
            this.isCountdown = true;
            this.countdownDuration$.next(+value);
            this.resetCountDown();
            return;
        } else {
            this.isCountdown = false;
        }

        if (this.resetState && value && value !== 'loading' && value !== 'close') {
            // reset state
            this.timer = setTimeout(() => {
                this._state = null;
                this._cdr.detectChanges();
            }, 2500);
        }
        this._state = value;
    }
    private _state: IndicatorState;
    private _resetState = true;
    isCountdown: boolean = false;
    private _isProgress: boolean = false;

    progress$ = new ReplaySubject<{ percent: string; offset: number }>(1);

    cancelTimer$: Subject<boolean> = new Subject();
    countdownDuration$ = new ReplaySubject<number>(1);
    resetCountdownTimer$ = new Subject();
    countdownTimeLeft$: Observable<number> = timer(0, 1000).pipe(
        withLatestFrom(this.countdownDuration$),
        map(([i, duration]) => {
            if (duration - i === 0) {
                this._state = null;
            }
            return duration - i;
        }),
        takeUntil(this.cancelTimer$),
        takeWhile((i) => i >= 0),
        finalize(() => (this._state = null)),
        repeatWhen(() => this.resetCountdownTimer$),
        shareReplay(1)
    );
    resetCountDown() {
        this.resetCountdownTimer$.next(true);
    }
    constructor(private _cdr: ChangeDetectorRef) {}

    log(event: any) {
        console.log(event);
    }
}
